import { ComponentProps, PropsWithChildren } from "react"
import { useTranslation } from "react-i18next"
import { Dialog } from "../ui/Dialog"
import clsx from "clsx"
import { commandOrCtrl } from "@/utils/system"
import { atom, useAtom } from "jotai"
import * as Tabs from "@radix-ui/react-tabs"
import { cn } from "@/utils/classname"

type ShortcutsTab = "navigation" | "selection" | "labeling" | "masks" | "editing" | "style"
const shortcutsTabAtom = atom<ShortcutsTab>("navigation")

export function ShortCutsDialog() {
	const { t } = useTranslation("components")
	const [currentTab, setCurrentTab] = useAtom(shortcutsTabAtom)

	const Tab = (props: ComponentProps<typeof Tabs.Trigger>) => (
		<Tabs.Trigger
			className={cn(
				"rounded-sm px-2.5 py-1.5 text-left text-sm hover:bg-base-800 data-[state=active]:bg-base-700 data-[state=active]:text-base-100",
			)}
			{...props}
		/>
	)

	const renderAIStyles = () => {
		return (
			<div className="flex w-full min-w-0 flex-col space-y-3">
				<div className="flex flex-col space-y-2">
					<div className="flex flex-row items-center justify-between">
						<div className="text-sm">{t("shortCutsDialog.markAsReferenceEdit")}</div>
						<ShortcutKey>M</ShortcutKey>
					</div>
					<div className="flex flex-row items-center justify-between">
						<div className="text-sm">{t("shortCutsDialog.unmarkReferenceEdit")}</div>
						<div className="flex flex-row items-center space-x-1">
							<ShortcutKey>{commandOrCtrl}</ShortcutKey>
							<ShortcutKey>M</ShortcutKey>
						</div>
					</div>
					<div className="flex flex-row items-center justify-between">
						<div className="text-sm">{t("shortCutsDialog.lockEdit")}</div>
						<ShortcutKey>L</ShortcutKey>
					</div>
					<div className="flex flex-row items-center justify-between">
						<div className="text-sm">{t("shortCutsDialog.unlockEdit")}</div>
						<div className="flex flex-row items-center space-x-1">
							<ShortcutKey>{commandOrCtrl}</ShortcutKey>
							<ShortcutKey>L</ShortcutKey>
						</div>
					</div>
				</div>
			</div>
		)
	}

	const renderNavigation = () => {
		return (
			<div className="flex w-full min-w-0 flex-col space-y-3">
				<div className="flex flex-col space-y-2">
					<div className="flex flex-row items-center justify-between">
						<div className="text-sm">{t("shortCutsDialog.switchToFocusView")}</div>
						<ShortcutKey>D</ShortcutKey>
					</div>
					<div className="flex flex-row items-center justify-between">
						<div className="text-sm">{t("shortCutsDialog.switchToGridView")}</div>
						<ShortcutKey>G</ShortcutKey>
					</div>
					<div className="flex flex-row items-center justify-between">
						<div className="text-sm">{t("shortCutsDialog.switchToCompareView")}</div>
						<ShortcutKey>C</ShortcutKey>
					</div>
					<div className="flex flex-row items-center justify-between">
						<div className="text-sm">{t("shortCutsDialog.searchPhotos")}</div>
						<div className="flex flex-row items-center space-x-1">
							<ShortcutKey>{commandOrCtrl}</ShortcutKey>
							<ShortcutKey>F</ShortcutKey>
						</div>
					</div>
					<div className="flex flex-row items-center justify-between">
						<div className="text-sm">{t("shortCutsDialog.beforeAfter")}</div>
						<div className="flex flex-row items-center space-x-1">
							<div className="text-xs">{t("shortCutsDialog.hold")}</div>
							<ShortcutKey>
								<div className="px-0.5 pb-0.5">\</div>
							</ShortcutKey>
							<div className="text-xs">{t("shortCutsDialog.toSeeBefore")}</div>
						</div>
					</div>
					<div className="flex flex-row items-center justify-between">
						<div className="text-sm">{t("shortCutsDialog.zoomInOut")}</div>
						<div className="flex flex-row items-center space-x-1">
							<ShortcutKey>Space</ShortcutKey> {/* i18n-ignore: keyboard key label */}
							<div className="text-xs">{t("shortCutsDialog.or")}</div>
							<ShortcutKey>Z</ShortcutKey>
						</div>
					</div>
					<div className="flex flex-row items-center justify-between">
						<div className="text-sm">
							{t("shortCutsDialog.toggleFullScreenPreview")}
						</div>
						<ShortcutKey>F</ShortcutKey>
					</div>
					<div className="flex flex-row items-center justify-between">
						<div className="text-sm">{t("shortCutsDialog.toggleFullScreenMode")}</div>
						<div className="flex flex-row items-center space-x-1">
							<ShortcutKey>SHIFT</ShortcutKey>
							<ShortcutKey>F</ShortcutKey>
						</div>
					</div>
					<div className="flex flex-row items-center justify-between">
						<div className="text-sm">{t("shortCutsDialog.openCropMenu")}</div>
						<ShortcutKey>R</ShortcutKey>
					</div>
					<div className="flex flex-row items-center justify-between">
						<div className="text-sm">{t("shortCutsDialog.openHealingMenu")}</div>
						<ShortcutKey>Q</ShortcutKey>
					</div>
					<div className="flex flex-row items-center justify-between">
						<div className="text-sm">{t("shortCutsDialog.openMaskingMenu")}</div>
						<ShortcutKey>W</ShortcutKey>
					</div>
					<div className="flex flex-row items-center justify-between">
						<div className="text-sm">{t("shortCutsDialog.openBasicEditingMenu")}</div>
						<ShortcutKey>E</ShortcutKey>
					</div>
				</div>
			</div>
		)
	}

	const renderSelection = () => {
		return (
			<div className="flex w-full min-w-0 flex-col space-y-3">
				<div className="flex flex-col space-y-2">
					<div className="flex flex-row items-center justify-between">
						<div className="text-sm">{t("shortCutsDialog.goToPreviousNextPhoto")}</div>
						<div className="flex flex-row items-center space-x-1">
							<ShortcutKey>{t("shortCutsDialog.text")}</ShortcutKey>
							<div className="text-xs">{t("shortCutsDialog.or")}</div>
							<ShortcutKey>{t("shortCutsDialog.text2")}</ShortcutKey>
						</div>
					</div>
					<div className="flex flex-row items-center justify-between">
						<div className="text-sm">{t("shortCutsDialog.selectAllPhotos")}</div>
						<div className="flex flex-row items-center space-x-1">
							<ShortcutKey>{commandOrCtrl}</ShortcutKey>
							<ShortcutKey>A</ShortcutKey>
						</div>
					</div>
					<div className="flex flex-row items-center justify-between">
						<div className="text-sm">
							{t("shortCutsDialog.selectMultipleIndividual")}
						</div>
						<div className="flex flex-row items-center space-x-1">
							<ShortcutKey>{commandOrCtrl}</ShortcutKey>
							<ShortcutKey>{t("shortCutsDialog.click")}</ShortcutKey>
						</div>
					</div>
					<div className="flex flex-row items-center justify-between">
						<div className="text-sm">
							{t("shortCutsDialog.selectMultipleContiguous")}
						</div>
						<div className="flex flex-row items-center space-x-1">
							<ShortcutKey>SHIFT</ShortcutKey>
							<ShortcutKey>{t("shortCutsDialog.click2")}</ShortcutKey>
						</div>
					</div>
				</div>
			</div>
		)
	}

	const renderLabeling = () => {
		return (
			<div className="flex w-full min-w-0 flex-col space-y-3">
				<div className="flex flex-col space-y-2">
					<div className="flex flex-row items-center justify-between">
						<div className="text-sm">{t("shortCutsDialog.setStarRating")}</div>
						<div className="flex flex-row items-center space-x-1">
							<ShortcutKey>1</ShortcutKey>
							<div className="text-xs">-</div>
							<ShortcutKey>5</ShortcutKey>
						</div>
					</div>
					<div className="flex flex-row items-center justify-between">
						<div className="text-sm">{t("shortCutsDialog.removeStarRating")}</div>
						<ShortcutKey>0</ShortcutKey>
					</div>
					<div className="flex flex-row items-center justify-between">
						<div className="text-sm">{t("shortCutsDialog.setColorLabel")}</div>
						<div className="flex flex-row items-center space-x-1">
							<ShortcutKey
								color="bg-red-700"
								textColor="text-base-800 dark:text-base-300"
							>
								6
							</ShortcutKey>
							<ShortcutKey
								color="bg-yellow-700"
								textColor="text-base-800 dark:text-base-300"
							>
								7
							</ShortcutKey>
							<ShortcutKey
								color="bg-green-700"
								textColor="text-base-800 dark:text-base-300"
							>
								8
							</ShortcutKey>
							<ShortcutKey
								color="bg-blue-700"
								textColor="text-base-800 dark:text-base-300"
							>
								9
							</ShortcutKey>
						</div>
					</div>
					<div className="flex flex-row items-center justify-between">
						<div className="text-sm">{t("shortCutsDialog.flagPhoto")}</div>
						<div className="flex flex-row items-center space-x-1">
							<ShortcutKey>P</ShortcutKey>
						</div>
					</div>
					<div className="flex flex-row items-center justify-between">
						<div className="text-sm">{t("shortCutsDialog.rejectPhoto")}</div>
						<div className="flex flex-row items-center space-x-1">
							<ShortcutKey>X</ShortcutKey>
						</div>
					</div>
					<div className="flex flex-row items-center justify-between">
						<div className="text-sm">{t("shortCutsDialog.unflagPhoto")}</div>
						<div className="flex flex-row items-center space-x-1">
							<ShortcutKey>U</ShortcutKey>
						</div>
					</div>
					<div className="flex flex-row items-center justify-between">
						<div className="text-sm">{t("shortCutsDialog.hidePhoto")}</div>
						<div className="flex flex-row items-center space-x-1">
							<ShortcutKey>H</ShortcutKey>
						</div>
					</div>
					<div className="flex flex-row items-center justify-between">
						<div className="text-sm">{t("shortCutsDialog.unhidePhoto")}</div>
						<div className="flex flex-row items-center space-x-1">
							<ShortcutKey>{commandOrCtrl}</ShortcutKey>
							<ShortcutKey>H</ShortcutKey>
						</div>
					</div>
					<div className="flex flex-row items-center justify-between">
						<div className="text-sm">{t("shortCutsDialog.removePhoto")}</div>
						<div className="flex flex-row items-center space-x-1">
							<ShortcutKey>Delete</ShortcutKey>{" "}
							{/* i18n-ignore: keyboard key label */}
							<div className="text-xs">{t("shortCutsDialog.or")}</div>
							<ShortcutKey>Backspace</ShortcutKey>{" "}
							{/* i18n-ignore: keyboard key label */}
						</div>
					</div>
				</div>
			</div>
		)
	}

	const renderMasks = () => {
		return (
			<div className="flex w-full min-w-0 flex-col space-y-3">
				<div className="flex flex-col space-y-2">
					<div className="flex flex-row items-center justify-between">
						<div className="text-sm">{t("shortCutsDialog.createSubjectMask")}</div>
						<div className="flex flex-row space-x-1">
							<ShortcutKey>S</ShortcutKey>
						</div>
					</div>
					<div className="flex flex-row items-center justify-between">
						<div className="text-sm">{t("shortCutsDialog.createBackgroundMask")}</div>
						<div className="flex flex-row space-x-1">
							<ShortcutKey>A</ShortcutKey>
						</div>
					</div>
					<div className="flex flex-row items-center justify-between">
						<div className="text-sm">{t("shortCutsDialog.createSkyMask")}</div>
						<div className="flex flex-row space-x-1">
							<ShortcutKey>Y</ShortcutKey>
						</div>
					</div>
					<div className="flex flex-row items-center justify-between">
						<div className="text-sm">{t("shortCutsDialog.createBrushMask")}</div>
						<div className="flex flex-row space-x-1">
							<ShortcutKey>B</ShortcutKey>
						</div>
					</div>
					<div className="flex flex-row items-center justify-between">
						<div className="text-sm">
							{t("shortCutsDialog.createLinearGradientMask")}
						</div>
						<div className="flex flex-row space-x-1">
							<ShortcutKey>O</ShortcutKey>
						</div>
					</div>
					<div className="flex flex-row items-center justify-between">
						<div className="text-sm">
							{t("shortCutsDialog.createRadialGradientMask")}
						</div>
						<div className="flex flex-row space-x-1">
							<ShortcutKey>K</ShortcutKey>
						</div>
					</div>
					<div className="flex flex-row items-center justify-between">
						<div className="text-sm">
							{t("shortCutsDialog.decreaseIncreaseBrushSize")}
						</div>
						<div className="flex flex-row space-x-1">
							<ShortcutKey>
								<div className="mb-0.5">[</div>
							</ShortcutKey>
							<ShortcutKey>
								<div className="mb-0.5">]</div>
							</ShortcutKey>
						</div>
					</div>
					<div className="flex flex-row items-center justify-between">
						<div className="text-sm">{t("shortCutsDialog.invertCurrentMask")}</div>
						<div className="flex flex-row space-x-1">
							<ShortcutKey>I</ShortcutKey>
						</div>
					</div>
					<div className="flex flex-row items-center justify-between">
						<div className="text-sm">{t("shortCutsDialog.moveHealingSource")}</div>
						<div className="flex flex-row space-x-1">
							<ShortcutKey>▲</ShortcutKey>
							<ShortcutKey>▼</ShortcutKey>
							<ShortcutKey>{t("shortCutsDialog.text3")}</ShortcutKey>
							<ShortcutKey>{t("shortCutsDialog.text4")}</ShortcutKey>
						</div>
					</div>
				</div>
			</div>
		)
	}

	const renderBasicEditing = () => {
		return (
			<div className="flex w-full min-w-0 flex-col space-y-3">
				<div className="flex flex-col space-y-2">
					<div className="flex flex-row items-center justify-between">
						<div className="text-sm">{t("shortCutsDialog.undo")}</div>
						<div className="flex flex-row space-x-1">
							<ShortcutKey>{commandOrCtrl}</ShortcutKey>
							<ShortcutKey>Z</ShortcutKey>
						</div>
					</div>
					<div className="flex flex-row items-center justify-between">
						<div className="text-sm">{t("shortCutsDialog.redo")}</div>
						<div className="flex flex-row space-x-1">
							<ShortcutKey>{commandOrCtrl}</ShortcutKey>
							<ShortcutKey>SHIFT</ShortcutKey>
							<ShortcutKey>Z</ShortcutKey>
						</div>
					</div>
					<div className="flex flex-row items-center justify-between">
						<div className="text-sm">{t("shortCutsDialog.copyAdjustments")}</div>
						<div className="flex flex-row items-center space-x-1">
							<ShortcutKey>{commandOrCtrl}</ShortcutKey>
							<ShortcutKey>C</ShortcutKey>
						</div>
					</div>
					<div className="flex flex-row items-center justify-between">
						<div className="text-sm">{t("shortCutsDialog.pasteAdjustments")}</div>
						<div className="flex flex-row items-center space-x-1">
							<ShortcutKey>{commandOrCtrl}</ShortcutKey>
							<ShortcutKey>V</ShortcutKey>
						</div>
					</div>

					<div className="flex flex-row items-center justify-between">
						<div className="text-sm">
							{t("shortCutsDialog.previousNextBasicAdjustment")}
						</div>
						<div className="flex flex-row space-x-1">
							<ShortcutKey>{"<"}</ShortcutKey>
							<ShortcutKey>{">"}</ShortcutKey>
						</div>
					</div>
					<div className="flex flex-row items-center justify-between">
						<div className="text-sm">
							{t("shortCutsDialog.increaseDecreaseBasicAdjustment")}
						</div>
						<div className="flex flex-row space-x-1">
							<ShortcutKey>+</ShortcutKey>
							<ShortcutKey>-</ShortcutKey>
						</div>
					</div>
					<div className="flex flex-row items-center justify-between">
						<div className="text-sm">{t("shortCutsDialog.sliderAdjustmentsSmall")}</div>
						<div className="flex flex-row space-x-1">
							<ShortcutKey>▲</ShortcutKey>
							<ShortcutKey>▼</ShortcutKey>
						</div>
					</div>
					<div className="flex flex-row items-center justify-between">
						<div className="text-sm">{t("shortCutsDialog.sliderAdjustmentsLarge")}</div>
						<div className="flex flex-row space-x-1">
							<ShortcutKey>SHIFT</ShortcutKey>
							<ShortcutKey>▲</ShortcutKey>
							<ShortcutKey>▼</ShortcutKey>
						</div>
					</div>

					<div className="flex flex-row items-center justify-between">
						<div className="text-sm">{t("shortCutsDialog.showHideClipping")}</div>
						<div className="flex flex-row space-x-1">
							<ShortcutKey>J</ShortcutKey>
						</div>
					</div>
				</div>
			</div>
		)
	}

	const renderCulling = () => {
		return (
			<div className="flex w-full min-w-0 flex-col space-y-3">
				<div className="flex flex-col space-y-2">
					<div className="flex flex-row items-center justify-between">
						<div className="text-sm">{t("shortCutsDialog.enterGroup")}</div>
						<div className="flex flex-row items-center space-x-1">
							<ShortcutKey>▲</ShortcutKey>
						</div>
					</div>
					<div className="flex flex-row items-center justify-between">
						<div className="text-sm">{t("shortCutsDialog.exitGroup")}</div>
						<div className="flex flex-row items-center space-x-1">
							<ShortcutKey>▼</ShortcutKey>
						</div>
					</div>
					<div className="flex flex-row items-center justify-between">
						<div className="text-sm">{t("shortCutsDialog.pressHoldAutoExposure")}</div>
						<div className="flex flex-row items-center space-x-1">
							<ShortcutKey>E</ShortcutKey>
						</div>
					</div>
					<div className="flex flex-row items-center justify-between">
						<div className="text-sm">{t("shortCutsDialog.markTopPick")}</div>
						<div className="flex flex-row items-center space-x-1">
							<ShortcutKey>T</ShortcutKey>
						</div>
					</div>
					<div className="flex flex-row items-center justify-between">
						<div className="text-sm">{t("shortCutsDialog.markReject")}</div>
						<div className="flex flex-row items-center space-x-1">
							<ShortcutKey>R</ShortcutKey>
						</div>
					</div>
					<div className="flex flex-row items-center justify-between">
						<div className="text-sm">{t("shortCutsDialog.markCandidate")}</div>
						<div className="flex flex-row items-center space-x-1">
							<ShortcutKey>C</ShortcutKey>
						</div>
					</div>
					<div className="flex flex-row items-center justify-between">
						<div className="text-sm">{t("shortCutsDialog.revertCullingOverrides")}</div>
						<div className="flex flex-row items-center space-x-1">
							<ShortcutKey>A</ShortcutKey>
						</div>
					</div>
				</div>
			</div>
		)
	}

	return (
		<Dialog trackingViewName="shortcuts_dialog" contentProps={{ className: "h-[480px]" }}>
			<Tabs.Root
				defaultValue={currentTab}
				className="flex min-h-0 w-[680px] flex-1 flex-row"
				onValueChange={(tab) => setCurrentTab(tab as ShortcutsTab)}
			>
				<Tabs.List className="m-[-58px] mr-6 flex shrink-0 flex-col space-y-2 bg-base-900 p-[52px] pr-6">
					<Tab value="navigation">{t("shortCutsDialog.tabNavigation")}</Tab>
					<Tab value="selection">{t("shortCutsDialog.tabPhotoSelection")}</Tab>
					<Tab value="labeling">{t("shortCutsDialog.tabRatingLabeling")}</Tab>
					<Tab value="culling">{t("shortCutsDialog.tabCulling")}</Tab>
					<Tab value="style">{t("shortCutsDialog.tabAiStyles")}</Tab>
					<Tab value="editing">{t("shortCutsDialog.tabEditing")}</Tab>
					<Tab value="masks">{t("shortCutsDialog.tabMasksHealing")}</Tab>
				</Tabs.List>

				<div className="flex min-h-0 min-w-0 flex-1 flex-col">
					<Tabs.Content value="navigation">{renderNavigation()}</Tabs.Content>
					<Tabs.Content value="selection">{renderSelection()}</Tabs.Content>
					<Tabs.Content value="labeling">{renderLabeling()}</Tabs.Content>
					<Tabs.Content value="culling">{renderCulling()}</Tabs.Content>
					<Tabs.Content value="style">{renderAIStyles()}</Tabs.Content>
					<Tabs.Content value="editing">{renderBasicEditing()}</Tabs.Content>
					<Tabs.Content value="masks">{renderMasks()}</Tabs.Content>

					<div className="mt-auto border-t border-base-600 pt-4 text-xs text-base-400">
						{t("shortCutsDialog.shortcutsAreBasedOnA")}
					</div>
				</div>
			</Tabs.Root>
		</Dialog>
	)
}

export const ShortcutKey = (props: PropsWithChildren<{ color?: string; textColor?: string }>) => {
	return (
		<div
			aria-hidden
			className={clsx(
				props.color ?? "bg-base-600",
				props.textColor ?? "text-base-300",
				"shrink-0 whitespace-pre rounded-sm px-1 py-px text-center text-xs",
			)}
		>
			{props.children}
		</div>
	)
}
